'use client';

import _defineProperty from "/codebuild/output/src3402/src/s3/01/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/codebuild/output/src3402/src/s3/01/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["classNames", "className", "style", "styles", "unstyled", "required", "__staticSelector", "__stylesApiProps", "size", "wrapperProps", "error", "disabled", "leftSection", "leftSectionProps", "leftSectionWidth", "rightSection", "rightSectionProps", "rightSectionWidth", "rightSectionPointerEvents", "leftSectionPointerEvents", "variant", "vars", "pointer", "multiline", "radius", "id", "withAria", "withErrorStyles", "mod", "inputSize"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { jsxs, jsx } from 'react/jsx-runtime';
import { rem } from '../../core/utils/units-converters/rem.mjs';
import 'react';
import { getSize, getFontSize, getRadius } from '../../core/utils/get-size/get-size.mjs';
import '@mantine/hooks';
import { createVarsResolver } from '../../core/styles-api/create-vars-resolver/create-vars-resolver.mjs';
import 'clsx';
import '../../core/MantineProvider/Mantine.context.mjs';
import '../../core/MantineProvider/default-theme.mjs';
import '../../core/MantineProvider/MantineProvider.mjs';
import '../../core/MantineProvider/MantineThemeProvider/MantineThemeProvider.mjs';
import { useProps } from '../../core/MantineProvider/use-props/use-props.mjs';
import '../../core/MantineProvider/MantineCssVariables/MantineCssVariables.mjs';
import { useStyles } from '../../core/styles-api/use-styles/use-styles.mjs';
import { extractStyleProps } from '../../core/Box/style-props/extract-style-props/extract-style-props.mjs';
import { Box } from '../../core/Box/Box.mjs';
import { polymorphicFactory } from '../../core/factory/polymorphic-factory.mjs';
import '../../core/DirectionProvider/DirectionProvider.mjs';
import { InputDescription } from './InputDescription/InputDescription.mjs';
import { InputError } from './InputError/InputError.mjs';
import { InputLabel } from './InputLabel/InputLabel.mjs';
import { InputPlaceholder } from './InputPlaceholder/InputPlaceholder.mjs';
import { useInputWrapperContext } from './InputWrapper.context.mjs';
import { InputWrapper } from './InputWrapper/InputWrapper.mjs';
import classes from './Input.module.css.mjs';
var defaultProps = {
  variant: "default",
  leftSectionPointerEvents: "none",
  rightSectionPointerEvents: "none",
  withAria: true,
  withErrorStyles: true
};
var varsResolver = createVarsResolver(function (_, props, ctx) {
  return {
    wrapper: {
      "--input-margin-top": ctx.offsetTop ? "calc(var(--mantine-spacing-xs) / 2)" : void 0,
      "--input-margin-bottom": ctx.offsetBottom ? "calc(var(--mantine-spacing-xs) / 2)" : void 0,
      "--input-height": getSize(props.size, "input-height"),
      "--input-fz": getFontSize(props.size),
      "--input-radius": props.radius === void 0 ? void 0 : getRadius(props.radius),
      "--input-left-section-width": props.leftSectionWidth !== void 0 ? rem(props.leftSectionWidth) : void 0,
      "--input-right-section-width": props.rightSectionWidth !== void 0 ? rem(props.rightSectionWidth) : void 0,
      "--input-padding-y": props.multiline ? getSize(props.size, "input-padding-y") : void 0,
      "--input-left-section-pointer-events": props.leftSectionPointerEvents,
      "--input-right-section-pointer-events": props.rightSectionPointerEvents
    }
  };
});
var Input = polymorphicFactory(function (_props, ref) {
  var props = useProps("Input", defaultProps, _props);
  var classNames = props.classNames,
    className = props.className,
    style = props.style,
    styles = props.styles,
    unstyled = props.unstyled,
    required = props.required,
    __staticSelector = props.__staticSelector,
    __stylesApiProps = props.__stylesApiProps,
    size = props.size,
    wrapperProps = props.wrapperProps,
    error = props.error,
    disabled = props.disabled,
    leftSection = props.leftSection,
    leftSectionProps = props.leftSectionProps,
    leftSectionWidth = props.leftSectionWidth,
    rightSection = props.rightSection,
    rightSectionProps = props.rightSectionProps,
    rightSectionWidth = props.rightSectionWidth,
    rightSectionPointerEvents = props.rightSectionPointerEvents,
    leftSectionPointerEvents = props.leftSectionPointerEvents,
    variant = props.variant,
    vars = props.vars,
    pointer = props.pointer,
    multiline = props.multiline,
    radius = props.radius,
    id = props.id,
    withAria = props.withAria,
    withErrorStyles = props.withErrorStyles,
    mod = props.mod,
    inputSize = props.inputSize,
    others = _objectWithoutProperties(props, _excluded);
  var _extractStyleProps = extractStyleProps(others),
    styleProps = _extractStyleProps.styleProps,
    rest = _extractStyleProps.rest;
  var ctx = useInputWrapperContext();
  var stylesCtx = {
    offsetBottom: ctx === null || ctx === void 0 ? void 0 : ctx.offsetBottom,
    offsetTop: ctx === null || ctx === void 0 ? void 0 : ctx.offsetTop
  };
  var getStyles = useStyles({
    name: ["Input", __staticSelector],
    props: __stylesApiProps || props,
    classes: classes,
    className: className,
    style: style,
    classNames: classNames,
    styles: styles,
    unstyled: unstyled,
    stylesCtx: stylesCtx,
    rootSelector: "wrapper",
    vars: vars,
    varsResolver: varsResolver
  });
  var ariaAttributes = withAria ? {
    required: required,
    disabled: disabled,
    "aria-invalid": !!error,
    "aria-describedby": ctx === null || ctx === void 0 ? void 0 : ctx.describedBy,
    id: (ctx === null || ctx === void 0 ? void 0 : ctx.inputId) || id
  } : {};
  return /* @__PURE__ */jsxs(Box, _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, getStyles("wrapper")), styleProps), wrapperProps), {}, {
    mod: [{
      error: !!error && withErrorStyles,
      pointer: pointer,
      disabled: disabled,
      multiline: multiline,
      "data-with-right-section": !!rightSection,
      "data-with-left-section": !!leftSection
    }, mod],
    variant: variant,
    size: size,
    children: [leftSection && /* @__PURE__ */jsx("div", _objectSpread(_objectSpread(_objectSpread({}, leftSectionProps), {}, {
      "data-position": "left"
    }, getStyles("section", {
      className: leftSectionProps === null || leftSectionProps === void 0 ? void 0 : leftSectionProps.className,
      style: leftSectionProps === null || leftSectionProps === void 0 ? void 0 : leftSectionProps.style
    })), {}, {
      children: leftSection
    })), /* @__PURE__ */jsx(Box, _objectSpread(_objectSpread(_objectSpread({
      component: "input"
    }, rest), ariaAttributes), {}, {
      ref: ref,
      required: required,
      mod: {
        disabled: disabled,
        error: !!error && withErrorStyles
      },
      variant: variant,
      __size: inputSize
    }, getStyles("input"))), rightSection && /* @__PURE__ */jsx("div", _objectSpread(_objectSpread(_objectSpread({}, rightSectionProps), {}, {
      "data-position": "right"
    }, getStyles("section", {
      className: rightSectionProps === null || rightSectionProps === void 0 ? void 0 : rightSectionProps.className,
      style: rightSectionProps === null || rightSectionProps === void 0 ? void 0 : rightSectionProps.style
    })), {}, {
      children: rightSection
    }))]
  }));
});
Input.classes = classes;
Input.Wrapper = InputWrapper;
Input.Label = InputLabel;
Input.Error = InputError;
Input.Description = InputDescription;
Input.Placeholder = InputPlaceholder;
Input.displayName = "@mantine/core/Input";
export { Input };